import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { getOpsAccounts } from '../../../requests';
import {
  Card,
  EmptyState,
  inputClass,
  PageBody,
  PageHeader,
  Pagination,
  rowClickClass,
  tableClass,
  tdClass,
  thClass,
  theadClass,
  Toolbar,
  SortTh,
  useListSort,
} from '../ui';

const OpsAccounts: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const { sortKey, sortDir, toggleSort } = useListSort('upcoming', 'desc');

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['opsAccounts', q, sortKey, sortDir, page],
    queryFn: () =>
      getOpsAccounts({
        sort: sortKey || undefined,
        dir: sortKey ? sortDir : undefined,
        q: q || undefined,
        page,
      }),
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Accounts"
        meta="New accounts are born in the sales pipeline — this screen views and edits what exists"
      />
      <Toolbar>
        <input
          className={`${inputClass} w-80`}
          placeholder="Search companies…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Toolbar>
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <PageBody wide>
          <Card>
            <div className="overflow-x-auto">
              <table className={`${tableClass} min-w-[900px]`}>
                <thead className={theadClass}>
                  <tr>
                    <SortTh
                      k="name"
                      label="Company"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="type"
                      label="Type"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="industry"
                      label="Industry"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="tier"
                      label="Tier"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="programs"
                      label="Live programs"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                      right
                    />
                    <SortTh
                      k="upcoming"
                      label="Upcoming classes"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                      right
                    />
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((a) => (
                    <tr
                      key={a.id}
                      className={rowClickClass}
                      onClick={() =>
                        navigate(PATH.opsAccountView.replace(':id', a.id))
                      }
                    >
                      <td className={`${tdClass} font-medium text-gray-four`}>
                        {a.name}
                      </td>
                      <td className={tdClass}>{a.type ?? '—'}</td>
                      <td className={tdClass}>{a.industry ?? '—'}</td>
                      <td className={tdClass}>{a.tier ?? '—'}</td>
                      <td
                        className={`${tdClass} text-right`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {a.livePrograms ? (
                          <Link
                            className="text-blue-primary hover:underline"
                            to={`${PATH.opsPrograms}?accountId=${a.id}&active=1`}
                          >
                            {a.livePrograms}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td
                        className={`${tdClass} text-right`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {a.upcomingClasses ? (
                          <Link
                            className="text-blue-primary hover:underline"
                            to={`${PATH.opsClasses}?accountId=${
                              a.id
                            }&label=${encodeURIComponent(a.name ?? '')}`}
                          >
                            {a.upcomingClasses}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data && data.items.length === 0 && (
              <EmptyState
                title="No accounts match"
                hint="Try a different company name."
              />
            )}
            <Pagination
              page={page}
              pages={pages}
              total={data?.total}
              onPage={setPage}
            />
          </Card>
        </PageBody>
      )}
    </>
  );
};

export default OpsAccounts;
